/**
 * OneAPI 令牌管理 API 服务
 * 提供用户令牌的增删改查功能
 */

import { copyTextToClipboard } from "@/lib/clipboard";

/**
 * 令牌状态枚举
 */
export enum TokenStatus {
	ENABLED = 1, // 启用
	DISABLED = 2, // 已禁用
	EXPIRED = 3, // 已过期
	EXHAUSTED = 4, // 已用尽
}

/**
 * 令牌信息接口
 */
export interface Token {
	id: number; // 令牌ID
	key: string; // 令牌密钥（仅创建时返回完整值）
	user_id: number; // 用户ID
	status: TokenStatus; // 令牌状态
	name: string; // 令牌名称
	created_time: number; // 创建时间（时间戳）
	accessed_time?: number; // 最后访问时间（时间戳）
	expired_time: number; // 过期时间（-1表示永不过期）
	remain_quota: number; // 剩余额度
	unlimited_quota: boolean; // 是否无限额度
	used_quota: number; // 已使用额度
}

/**
 * API 响应基础接口
 */
interface BaseResponse<T> {
	success: boolean;
	data: T;
	message: string;
}

/**
 * 获取令牌列表响应
 */
export type GetTokensResponse = BaseResponse<Token[]>;

/**
 * 创建令牌响应
 */
export type CreateTokenResponse = BaseResponse<Token>;

/**
 * 删除令牌响应
 */
export type DeleteTokenResponse = BaseResponse<null>;

/**
 * 更新令牌状态响应
 */
export type UpdateTokenStatusResponse = BaseResponse<null>;

/**
 * 获取认证头
 */
function getAuthHeaders(): HeadersInit {
	const authStore = localStorage.getItem("auth-storage");
	if (authStore) {
		const { state } = JSON.parse(authStore);
		const token = state?.accessToken;
		if (token) {
			return {
				Authorization: `Bearer ${token}`,
				"Content-Type": "application/json",
			};
		}
	}
	return {
		"Content-Type": "application/json",
	};
}

/**
 * 获取用户的所有令牌
 * @returns 令牌列表
 */
export async function getTokens(): Promise<GetTokensResponse> {
	const response = await fetch("/api/user/tokens", {
		method: "GET",
		headers: getAuthHeaders(),
	});

	if (!response.ok) {
		throw new Error(`获取令牌列表失败: ${response.statusText}`);
	}

	return response.json();
}

/**
 * 创建新令牌（为当前用户）
 * @param name 令牌名称
 * @returns 新创建的令牌信息
 */
export async function createToken(name: string): Promise<CreateTokenResponse> {
	const response = await fetch("/api/user/tokens", {
		method: "POST",
		headers: getAuthHeaders(),
		body: JSON.stringify({ name }),
	});

	if (!response.ok) {
		const error = await response.json();
		throw new Error(error.message || "创建令牌失败");
	}

	return response.json();
}

/**
 * 管理员为指定用户创建令牌
 * @param userId 目标用户ID
 * @param name 令牌名称
 * @param remainQuota 剩余额度（默认-1）
 * @param expiredTime 过期时间（默认-1表示永不过期）
 * @param unlimitedQuota 是否无限额度（默认true）
 * @returns 新创建的令牌信息
 */
export async function createTokenForUser(
	userId: number,
	name: string,
	remainQuota = -1,
	expiredTime = -1,
	unlimitedQuota = true,
): Promise<CreateTokenResponse> {
	const response = await fetch("/api/admin/token/user", {
		method: "POST",
		headers: getAuthHeaders(),
		body: JSON.stringify({
			user_id: userId,
			name,
			remain_quota: remainQuota,
			expired_time: expiredTime,
			unlimited_quota: unlimitedQuota,
		}),
	});

	if (!response.ok) {
		const error = await response.json();
		throw new Error(error.message || "为用户创建令牌失败");
	}

	return response.json();
}

/**
 * 删除令牌
 * @param tokenId 令牌ID
 * @returns 删除结果
 */
export async function deleteToken(
	tokenId: number,
): Promise<DeleteTokenResponse> {
	const response = await fetch(`/api/user/tokens/${tokenId}`, {
		method: "DELETE",
		headers: getAuthHeaders(),
	});

	if (!response.ok) {
		throw new Error(`删除令牌失败: ${response.statusText}`);
	}

	return response.json();
}

/**
 * 更新令牌状态
 * @param tokenId 令牌ID
 * @param status 新状态（1=启用，2=禁用，3=已过期，4=已用尽）
 * @returns 更新结果
 */
export async function updateTokenStatus(
	tokenId: number,
	status: TokenStatus,
): Promise<UpdateTokenStatusResponse> {
	const response = await fetch(`/api/user/tokens/${tokenId}/status`, {
		method: "PUT",
		headers: getAuthHeaders(),
		body: JSON.stringify({ status }),
	});

	if (!response.ok) {
		throw new Error(`更新令牌状态失败: ${response.statusText}`);
	}

	return response.json();
}

/**
 * 格式化令牌状态显示
 * @param status 令牌状态
 * @returns 状态文本
 */
export function formatTokenStatus(status: TokenStatus): string {
	switch (status) {
		case TokenStatus.ENABLED:
			return "启用";
		case TokenStatus.DISABLED:
			return "已禁用";
		case TokenStatus.EXPIRED:
			return "已过期";
		case TokenStatus.EXHAUSTED:
			return "已用尽";
		default:
			return "未知";
	}
}

/**
 * 格式化令牌状态颜色类
 * @param status 令牌状态
 * @returns Tailwind CSS 颜色类
 */
export function getTokenStatusColor(status: TokenStatus): string {
	switch (status) {
		case TokenStatus.ENABLED:
			return "text-green-600";
		case TokenStatus.DISABLED:
			return "text-gray-600";
		case TokenStatus.EXPIRED:
			return "text-red-600";
		case TokenStatus.EXHAUSTED:
			return "text-orange-600";
		default:
			return "text-gray-600";
	}
}

/**
 * 格式化时间戳为日期字符串
 * @param timestamp Unix 时间戳（秒）
 * @returns 格式化的日期字符串
 */
export function formatTimestamp(timestamp: number): string {
	if (timestamp === -1) {
		return "永不过期";
	}

	const date = new Date(timestamp * 1000);
	return date.toLocaleString("zh-CN", {
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
	});
}

/**
 * 隐藏令牌密钥中间部分
 * @param key 完整令牌密钥
 * @returns 隐藏中间部分的令牌密钥（如：sk-abc...xyz）
 */
export function maskTokenKey(key: string): string {
	if (key.length <= 10) {
		return key;
	}
	return `${key.substring(0, 7)}...${key.substring(key.length - 4)}`;
}

/**
 * 复制令牌密钥到剪贴板
 * @param key 令牌密钥
 * @returns Promise<boolean> 是否复制成功
 */
export async function copyTokenKey(key: string): Promise<boolean> {
	const ok = await copyTextToClipboard(key);
	if (!ok) {
		console.error("复制失败: clipboard 与 execCommand 均不可用");
	}
	return ok;
}
